'use client';

import { useTranslations } from 'next-intl';
import Image from 'next/image';
import { SectionWrapper } from '@/components/ui/section-wrapper';
import { Typography } from '@/components/ui/typography';
import { SectionHeading } from '@/components/frontend/common/section-heading';
import { useScrollAnimation } from '@/hooks/use-scroll-animation';
import { cn } from '@/lib/utils';

interface WhatIsBitcoinMlmSectionProps {
    translationNamespace: string;
    paragraphKeys?: string[];
    imageSrc: string;
    imageAlt?: string;
    sectionHeadingTitle?: string;
}

export function WhatIsBitcoinMlmSection({
    translationNamespace,
    paragraphKeys = ['description', 'description2'],
    imageSrc,
    imageAlt,
    sectionHeadingTitle,
}: WhatIsBitcoinMlmSectionProps) {
    const t = useTranslations(translationNamespace);
    const sectionRef = useScrollAnimation({ threshold: 0.1 });

    return (
        <SectionWrapper variant="gradient" padding="md" ref={sectionRef.ref} className="relative overflow-hidden bg-white dark:bg-black">
            <div className="container static z-10">
                {/* Section Heading */}

                {/* Content Grid */}
                <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
                    {/* Left Column - Text Content */}
                    <div
                        className={cn(
                            "transition-all duration-700 ease-out",
                            sectionRef.isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-6'
                        )}
                    >
                        <div className="space-y-4">
                            <SectionHeading
                                page=""
                                fallbackTitle={sectionHeadingTitle}
                                align="left"
                                maxWidthClass="max-w-4xl"
                                className='!mb-8'
                            />
                            {/* Paragraphs */}
                            {paragraphKeys.map((key, index) => {
                                const text = t(key);
                                if (!text || text === key) return null;

                                return (
                                    <div
                                        key={key}
                                        className={cn(
                                            "transition-all duration-700 ease-out",
                                            sectionRef.isVisible
                                                ? 'opacity-100 translate-y-0'
                                                : 'opacity-0 translate-y-4'
                                        )}
                                        style={{ transitionDelay: `${200 + index * 100}ms` }}
                                    >
                                        <Typography
                                            variant="p"
                                            className="text-gray-600 dark:text-gray-400 leading-relaxed text-base"
                                        >
                                            {text}
                                        </Typography>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Right Column - Image */}
                    <div
                        className={cn(
                            "relative transition-all duration-700 ease-out",
                            sectionRef.isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-6'
                        )}
                        style={{ transitionDelay: '300ms' }}
                    >
                        <div className="relative rounded-2xl overflow-hidden shadow-lg bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-4">
                            <Image
                                src={imageSrc}
                                alt={imageAlt || 'Feature illustration'}
                                width={600}
                                height={500}
                                className="w-full h-full object-cover rounded-xl"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </SectionWrapper>
    );
}

